import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useSidebarState } from '@/hooks/useSidebarState';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { PaidRestFilterKey } from '../../models/vacationPaidRest.types';
import {
    CARD_ACTIVE_CLASSES,
    CARD_FOCUS_CLASSES,
    CARD_ICON_WRAP_CLASSES,
    CARD_LABEL_CLASSES,
    CARD_TONE_CLASSES,
    CARD_VALUE_CLASSES,
    PAID_REST_CARD_METADATA,
    PAID_REST_FILTER_GROUPS,
    PARTITION_HINT,
} from './vacationPaidRestFilterSidebar.definitions';
import type { VacationPaidRestFilterSidebarProps } from './vacationPaidRestFilterSidebar.types';

const STORAGE_KEY = 'radar-vacation-paid-rest-filters-collapsed';
const TABLE_MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

interface FilterButtonProps {
    filterKey: PaidRestFilterKey;
    count: number;
    isActive: boolean;
    isCollapsed: boolean;
    onToggle: (key: PaidRestFilterKey) => void;
}

function FilterButton({ filterKey, count, isActive, isCollapsed, onToggle }: FilterButtonProps) {
    const { label, tone, description, icon: Icon } = PAID_REST_CARD_METADATA[filterKey];

    return (
        <button
            type="button"
            aria-pressed={isActive}
            aria-label={`${label}: ${count} ${description}`}
            title={isCollapsed ? label : undefined}
            onClick={() => onToggle(filterKey)}
            className={clsx(
                'flex items-center gap-3 rounded-lg border p-2.5 text-left transition-all',
                isCollapsed ? 'flex-col' : '',
                CARD_TONE_CLASSES[tone],
                CARD_FOCUS_CLASSES,
                isActive && CARD_ACTIVE_CLASSES[tone]
            )}
        >
            <span
                className={clsx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                    CARD_ICON_WRAP_CLASSES[tone]
                )}
            >
                <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            {!isCollapsed && (
                <span className="min-w-0">
                    <span
                        className={clsx(
                            'block text-[11px] font-medium leading-tight font-montserrat',
                            CARD_LABEL_CLASSES[tone]
                        )}
                    >
                        {label}
                    </span>
                    <span
                        className={clsx(
                            'mt-0.5 block text-lg font-semibold font-montserrat',
                            CARD_VALUE_CLASSES[tone]
                        )}
                    >
                        {count}
                    </span>
                </span>
            )}
            {isCollapsed && (
                <span
                    className={clsx(
                        'text-lg font-semibold font-montserrat',
                        CARD_VALUE_CLASSES[tone]
                    )}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

export function VacationPaidRestFilterSidebar({
    counts,
    selectedSituations,
    onToggleSituation,
    selectedRights,
    onToggleRight,
    isLoading = false,
    className,
    tourId,
}: VacationPaidRestFilterSidebarProps) {
    const isMobile = useMediaQuery(TABLE_MOBILE_MEDIA_QUERY);
    const { isCollapsed, toggleSidebar } = useSidebarState(STORAGE_KEY, false);
    const showCollapsed = !isMobile && isCollapsed;

    const groupSelection: Record<'situation' | 'right', { selected: PaidRestFilterKey[]; onToggle: (key: PaidRestFilterKey) => void }> = {
        situation: { selected: selectedSituations, onToggle: onToggleSituation },
        right: { selected: selectedRights, onToggle: onToggleRight },
    };

    if (isLoading) {
        return (
            <div
                className={clsx(
                    'w-full lg:shrink-0 lg:sticky lg:top-4 lg:self-start transition-all duration-300',
                    showCollapsed ? 'lg:w-14' : 'lg:w-52',
                    className
                )}
                aria-hidden="true"
                data-tour={tourId}
            >
                <div className={clsx('flex flex-col gap-2', showCollapsed ? 'items-center' : '')}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="animate-pulse rounded-lg border border-raro-gray-200 bg-white"
                            style={{
                                height: showCollapsed ? '48px' : '64px',
                                width: showCollapsed ? '40px' : '100%',
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'w-full lg:shrink-0 lg:sticky lg:top-4 lg:self-start transition-all duration-300 flex flex-col gap-3',
                showCollapsed ? 'lg:w-14' : 'lg:w-52',
                className
            )}
            data-tour={tourId}
        >
            {!isMobile && (
                <button
                    type="button"
                    onClick={toggleSidebar}
                    aria-expanded={!isCollapsed}
                    title={isCollapsed ? 'Expandir filtros' : 'Recolher filtros'}
                    className="flex h-10 w-full items-center justify-center rounded-lg border border-raro-gray-200 bg-white p-2 text-raro-gray-600 transition-colors hover:border-raro-gray-300 hover:bg-raro-gray-50 hover:text-raro-gray-700 lg:w-10 lg:self-end"
                >
                    {isCollapsed ? (
                        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                    )}
                    <span className="sr-only">{isCollapsed ? 'Expandir filtros' : 'Recolher filtros'}</span>
                </button>
            )}

            {PAID_REST_FILTER_GROUPS.map((group) => (
                <div key={group.id} className="flex flex-col gap-2">
                    {!showCollapsed && (
                        <h3 className="text-xs font-semibold font-montserrat uppercase tracking-widest text-raro-gray-700">
                            {group.label}
                        </h3>
                    )}
                    {showCollapsed && <hr className="border-t border-raro-gray-200" />}
                    {showCollapsed && <span className="sr-only">{group.label}</span>}

                    <div
                        className={clsx(
                            showCollapsed ? 'flex flex-col gap-2 items-center' : 'flex flex-col gap-2'
                        )}
                    >
                        {group.keys.map((key) => (
                            <FilterButton
                                key={key}
                                filterKey={key}
                                count={counts[key]}
                                isActive={groupSelection[group.id].selected.includes(key)}
                                isCollapsed={showCollapsed}
                                onToggle={groupSelection[group.id].onToggle}
                            />
                        ))}
                    </div>
                </div>
            ))}

            {!showCollapsed && (
                <div className="space-y-2 border-t border-raro-gray-100 pt-3">
                    <p className="text-[11px] text-raro-gray-500 font-montserrat">
                        {PARTITION_HINT}
                    </p>
                </div>
            )}
        </div>
    );
}

export default VacationPaidRestFilterSidebar;
